import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BoltIcon from "@mui/icons-material/Bolt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GradeIcon from "@mui/icons-material/Grade";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { alpha, useTheme } from "@mui/material/styles";
import { formatPercent, formatRatingOver5 } from "../report.utils";

function KpiCard({ title, value, helper, active, onClick, icon, tone = "primary" }) {
  const theme = useTheme();
  const tones = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };
  const accent = tones[tone] || theme.palette.primary.main;

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      sx={{
        px: 1.1,
        py: 1,
        minHeight: 92,
        cursor: onClick ? "pointer" : "default",
        borderRadius: "8px",
        borderColor: active ? accent : alpha(theme.palette.text.primary, 0.16),
        boxShadow: active ? `0 0 0 1px ${alpha(accent, 0.28)}` : "none",
        background:
          theme.palette.mode === "dark"
            ? alpha(accent, active ? 0.12 : 0.07)
            : `linear-gradient(145deg, ${alpha(accent, 0.09)} 0%, ${alpha(
                theme.palette.background.paper,
                0.94
              )} 44%, ${alpha(theme.palette.background.paper, 0.88)} 100%)`,
        transition: "all .22s ease",
        "&:hover": {
          borderColor: onClick ? accent : undefined,
          transform: onClick ? "translateY(-2px)" : "none",
          boxShadow: onClick ? `0 20px 32px -28px ${alpha(accent, 0.7)}` : "none",
        },
      }}
    >
      <Stack spacing={0.85}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "7px",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.16),
              color: accent,
            }}
          >
            {icon}
          </Box>
        </Stack>

        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
          {value}
        </Typography>

        <Typography noWrap variant="caption" color="text.secondary">
          {helper}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function ReportKpiCards({ kpis, activeQuickFilter, onQuickFilter }) {
  const cards = [
    {
      key: "total",
      title: "Total Appraisals",
      value: kpis.total,
      helper: "Click to clear quick filters",
      icon: <FactCheckIcon sx={{ fontSize: 16 }} />,
      tone: "primary",
    },
    {
      key: "avg_score",
      title: "Average Rating (/5)",
      value: formatRatingOver5(kpis.avg_score_over_5),
      helper: `Equivalent to ${formatPercent(kpis.avg_score, 1)}`,
      icon: <GradeIcon sx={{ fontSize: 16 }} />,
      tone: "info",
    },
    {
      key: "completed",
      title: "Completed %",
      value: formatPercent(kpis.completed_pct, 1),
      helper: "Click to filter completed",
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 16 }} />,
      tone: "success",
    },
    {
      key: "proposed",
      title: "Proposed Recommendations",
      value: kpis.proposed_recommendations_count,
      helper: "Click to filter proposed",
      icon: <BoltIcon sx={{ fontSize: 16 }} />,
      tone: "warning",
    },
    {
      key: "approved",
      title: "Approved Recommendations",
      value: kpis.approved_recommendations_count,
      helper: "Click to filter approved",
      icon: <TaskAltIcon sx={{ fontSize: 16 }} />,
      tone: "success",
    },
    {
      key: "avg_delta",
      title: "Avg Delta vs Previous",
      value: `${kpis.avg_delta_vs_previous > 0 ? "+" : ""}${kpis.avg_delta_vs_previous || 0}`,
      helper: "Click to filter improved",
      icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
      tone: "info",
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
          xl: "repeat(6, minmax(0, 1fr))",
        },
        gap: 0.8,
      }}
    >
      {cards.map((card) => (
        <KpiCard
          key={card.key}
          title={card.title}
          value={card.value}
          helper={card.helper}
          active={activeQuickFilter === card.key}
          onClick={() => onQuickFilter(card.key)}
          icon={card.icon}
          tone={card.tone}
        />
      ))}
    </Box>
  );
}
